import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboardStats } from '../../../redux/actions/DashboardActions';
import { Skeleton } from '@mui/material';

const DashboardStats = () => {
    const dispatch = useDispatch();
    const dashboard = useSelector(state => state.dashboard);
    const { data, loading, error } = dashboard;
    const dealerLogin = useSelector(state => state.dealerLogin);
    const { dealerInfo } = dealerLogin;

    const getMonthParam = () => {
        const now = new Date();
        return (now.getMonth() === 0 ? 12 : now.getMonth()).toString();
    };

    useEffect(() => {
        if (dealerInfo && dealerInfo._id) {
            dispatch(fetchDashboardStats(dealerInfo._id, getMonthParam(),dealerInfo.token));
        }
    }, [dealerInfo, dispatch]);

     const cardStyles = {
        card: {
            border: 'none',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            background: '#ffffff'
        },
        cardHover: {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)'
        },
        gradientLine: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            zIndex: 1
        },
        iconContainer: {
            width: '60px',
            height: '60px',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        },
        valueText: {
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '0.5rem'
        },
        titleText: {
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#6b7280',
            marginBottom: '0.75rem'
        },
        comparisonChip: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '600',
            gap: '4px'
        },
        positiveChip: {
            backgroundColor: '#dcfce7',
            color: '#166534'
        },
        negativeChip: {
            backgroundColor: '#fee2e2',
            color: '#dc2626'
        },
        skeletonPulse: {
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'pulse 2s infinite'
        }
    };

     if (loading) {
        return (
            <div className="row row-cols-1 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-4 g-3">
                {[1, 2, 3, 4].map((item) => (
                    <div className="col" key={item}>
                        <div 
                            className="card border-0 position-relative"
                            style={cardStyles.card}
                        >
                            <div 
                                style={{
                                    ...cardStyles.gradientLine,
                                    background: 'linear-gradient(135deg, #e0e0e0 0%, #f0f0f0 100%)'
                                }}
                            ></div>
                            
                            <div className="card-body p-4" style={{ paddingTop: '24px' }}>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="flex-grow-1">
                                        <Skeleton 
                                            variant="text" 
                                            width="80%" 
                                            height={20} 
                                            sx={{ marginBottom: '12px' }}
                                        />
                                        <Skeleton 
                                            variant="text" 
                                            width="50%" 
                                            height={40} 
                                            sx={{ marginBottom: '8px' }}
                                        />
                                    </div>
                                    <Skeleton 
                                        variant="rounded" 
                                        width={60} 
                                        height={60} 
                                        sx={{ borderRadius: '16px' }}
                                    />
                                </div>
                                
                                <hr className="my-3 opacity-25" />
                                
                                <Skeleton 
                                    variant="text" 
                                    width="70%" 
                                    height={16} 
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    if (error) return <div>{error}</div>;
    if (!data || Object.values(data).every(value => value === 0 || !value)) {
        return <div>No Data Available</div>;
    }

    const formatPercentage = (value) => {
        const formattedValue = parseFloat(value).toFixed(2);
        return formattedValue >= 0 ? `+${formattedValue}%` : `${formattedValue}%`;
    };

    return (
        <div className="row row-cols-1 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-4">
            <div className="col">
                <div className="card radius-10">
                    <div className="card-body">
                        <div className="d-flex align-items-center">
                            <div>
                                <p className="mb-0 text-secondary">Total Orders in {data.currentMonthName}</p>
                                <h4 className="my-1">{data.totalOrdersCurrentMonth}</h4>
                            </div>
                            <div className="widget-icon-large bg-gradient-purple text-white ms-auto">
                                <i className="bi bi-basket2-fill" />
                            </div>
                        </div>
                        <div className="border-top my-2"></div>
                        <small className="mb-0">
                            <span className={`text-${data.totalOrdersComparison >= 0 ? 'success' : 'danger'}`}>
                                {formatPercentage(data.totalOrdersComparison)} <i className={`bi bi-arrow-${data.totalOrdersComparison >= 0 ? 'up' : 'down'}`}></i>
                            </span>{" "}
                            Compared to {data.lastMonthName}
                        </small>
                    </div>
                </div>
            </div>
            <div className="col">
                <div className="card radius-10">
                    <div className="card-body">
                        <div className="d-flex align-items-center">
                            <div>
                                <p className="mb-0 text-secondary">Orders in Production</p>
                                <h4 className="my-1">{data.ordersInProduction}</h4>
                            </div>
                            <div className="widget-icon-large bg-gradient-success text-white ms-auto">
                                <i className="bi bi-currency-exchange" />
                            </div>
                        </div>
                        <div className="border-top my-2"></div>
                        <small className="mb-0">
                            <span className={`text-${data.ordersInProductionComparison >= 0 ? 'success' : 'danger'}`}>
                                {formatPercentage(data.ordersInProductionComparison)} <i className={`bi bi-arrow-${data.ordersInProductionComparison >= 0 ? 'up' : 'down'}`}></i>
                            </span>{" "}
                            {/* Compared to {data.lastMonthName} */}
                        </small>
                    </div>
                </div>
            </div>
            <div className="col">
                <div className="card radius-10">
                    <div className="card-body">
                        <div className="d-flex align-items-center">
                            <div>
                                <p className="mb-0 text-secondary">Orders Ready</p>
                                <h4 className="my-1">{data.ordersReady}</h4>
                            </div>
                            <div className="widget-icon-large bg-gradient-danger text-white ms-auto">
                                <i className="bi bi-bag-check" />
                            </div>
                        </div>
                        <div className="border-top my-2"></div>
                        <small className="mb-0">
                            <span className={`text-${data.ordersReadyComparison >= 0 ? 'success' : 'danger'}`}>
                                {formatPercentage(data.ordersReadyComparison)} <i className={`bi bi-arrow-${data.ordersReadyComparison >= 0 ? 'up' : 'down'}`}></i>
                            </span>{" "}
                            {/* Compared to {data.lastMonthName} */}
                        </small>
                    </div>
                </div>
            </div>
            <div className="col">
                <div className="card radius-10">
                    <div className="card-body">
                        <div className="d-flex align-items-center">
                            <div>
                                <p className="mb-0 text-secondary">Total Payment Pending</p>
                                <h4 className="my-1">{data.totalPaymentPending}</h4>
                            </div>
                            <div className="widget-icon-large bg-gradient-info text-white ms-auto">
                                <i className="bi bi-piggy-bank" />
                            </div>
                        </div>
                        <div className="border-top my-2"></div>
                        <small className="mb-0">
                            <span className={`text-${data.totalPaymentPendingComparison >= 0 ? 'success' : 'danger'}`}>
                                {formatPercentage(data.totalPaymentPendingComparison)} <i className={`bi bi-arrow-${data.totalPaymentPendingComparison >= 0 ? 'up' : 'down'}`}></i>
                            </span>{" "}
                            {/* Compared to {data.lastMonthName} */}
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardStats;
